import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import useUpdateStatusSection from 'hooks/useUpdateStatusSection';
import { useGetIROScoreListQuery, useGetSingleMAInitiateQuery, useRequestReviewMAMutation } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import MAIroScoringView from './MAIroScoringView';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IMAReviewError from './interfaces/IMAReviewError';
import type IMAReviewResponse from './interfaces/IMAReviewResponse';

function MAIroScoring() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { recordId = '' } = useParams();
  const [selectVC, setSelectVC] = useState<string>('');
  const [options, setOptions] = useState<IOption[]>([]);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState<boolean>(false);

  const { data: singleMA, isFetching: isLoadingSingleMA } = useGetSingleMAInitiateQuery({ id: recordId });
  const { data: userData } = useUserMeQuery();
  const { access } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });
  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const { data: iroScores, isFetching: isFetchingIroScores } = useGetIROScoreListQuery({
    maId: recordId,
    valueChainMapId: selectVC ? Number(selectVC) : undefined,
  });

  const { updateStatus } = useUpdateStatusSection({
    recordId,
    type: SECTION_TYPE.IRO_SCORING,
    active: true,
    status: STATUS_SECTIONS.COMPLETED,
  });

  const [requestReviewMA] = useRequestReviewMAMutation();

  const handleGoBack = () => {
    navigate(AppRoutes.materialityAssessmentIROIdentification.replace(':recordId', recordId));
  };

  const handleCancel = () => {
    navigate(AppRoutes.materialityAssessment);
  };

  const handleOpenFinishModal = () => {
    setIsFinishModalOpen(true);
  };

  const onCloseFinishModal = () => {
    setIsFinishModalOpen(false);
  };

  const onFinishConfirm = async () => {
    if (isViewOnly) {
      navigate(AppRoutes.materialityAssessment);
      setIsFinishModalOpen(false);
      return;
    }

    try {
      const request = await requestReviewMA({ id: Number(recordId) });
      const typedRequest = request as unknown as IMAReviewResponse;
      const errors: IMAReviewError[] = typedRequest.data?.data?.errors ?? [];

      if ('error' in request || errors.length > 0) {
        toast.error(t('materialityAssessment.reviewValidationError'));
        return;
      }

      updateStatus();
      navigate(AppRoutes.materialityAssessment);
      setIsFinishModalOpen(false);
    } catch {
      toast.error(t('materialityAssessment.reviewValidationError'));
    }
  };

  useEffect(() => {
    if (singleMA) {
      const vcList = singleMA?.data?.valueChainMaps?.map(
        (itemVC) => ({ label: itemVC.name, value: `${itemVC.id}` }),
      ) || [];
      setOptions([{ label: 'All', value: '' }, ...vcList]);
    }
  }, [singleMA]);

  return (
    <MAIroScoringView
      loading={isFetchingIroScores || isLoadingSingleMA}
      handleGoBack={handleGoBack}
      items={iroScores?.data || []}
      vcOptions={options}
      valueSelector={selectVC}
      setValueSelector={setSelectVC}
      handleOpenFinishModal={handleOpenFinishModal}
      handleCancel={handleCancel}
      isFinishModalOpen={isFinishModalOpen}
      onCloseFinishModal={onCloseFinishModal}
      onFinishConfirm={onFinishConfirm}
    />
  );
}

export default MAIroScoring;
